// ============================================
// pages/Salon/SalonTodayBills.js
// Today's Bills Dashboard — 12:00 AM to 11:59 PM
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../common/context/AuthContext';
import { SALON_API_URL } from '../../api/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ReceiptText, TrendingUp, Loader2,
  Banknote, Smartphone, CreditCard, SplitSquareVertical,
  Clock, User, Phone, ChevronDown, ChevronUp,
  Scissors, Package, Hash, Tag, MessageCircle, Edit2, X, Save, Minus, Plus, Trash2,
} from 'lucide-react';

// ─── Helpers ───────────────────────────────────────────────────────────────
const IST_TIME_ZONE = 'Asia/Kolkata';
const IST_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: IST_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
function getDateParts(formatter, value) {
  return formatter.formatToParts(new Date(value)).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});
}

function getIstDateParam(value = new Date()) {
  const parts = getDateParts(IST_DATE_FORMATTER, value);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function fmtTime(dateStr) {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleTimeString('en-IN', { timeZone: IST_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: true }); }
  catch { return '—'; }
}

const PAYMENT_META = {
  cash:  { Icon: Banknote,            label: 'Cash',  bg: 'bg-emerald-500', light: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  upi:   { Icon: Smartphone,          label: 'UPI',   bg: 'bg-blue-500',    light: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' },
  card:  { Icon: CreditCard,          label: 'Card',  bg: 'bg-violet-500',  light: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800' },
  split: { Icon: SplitSquareVertical, label: 'Split', bg: 'bg-orange-500',  light: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800' },
  other: { Icon: CreditCard,          label: 'Other', bg: 'bg-slate-500',   light: 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700' },
};

// ─── Big Stat Card ─────────────────────────────────────────────────────────
function StatCard({ title, value, sub, icon: Icon, gradient, iconBg }) {
  return (
    <div className={`relative overflow-hidden rounded-3xl p-6 ${gradient} shadow-sm`}>
      {/* Background decorative circle */}
      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10"/>
      <div className="absolute -right-1 -bottom-6 w-16 h-16 rounded-full bg-white/10"/>
      <div className="relative">
        <div className={`w-12 h-12 rounded-2xl ${iconBg} flex items-center justify-center mb-4`}>
          <Icon size={24} className="text-white"/>
        </div>
        <p className="text-sm font-semibold text-white/70 uppercase tracking-widest">{title}</p>
        <p className="text-3xl font-black text-white mt-1 leading-tight">{value}</p>
        {sub && <p className="text-xs text-white/60 mt-1.5 font-medium">{sub}</p>}
      </div>
    </div>
  );
}

// ─── WhatsApp Bill Share ───────────────────────────────────────────────────
function getGreeting() {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return 'Good Morning';
  if (h >= 12 && h < 17) return 'Good Afternoon';
  return 'Good Evening';
}

function buildWhatsAppMessage(bill) {
  const name      = bill.customer_name?.trim();
  const greeting  = getGreeting();
  const salutation = name ? `${greeting}, ${name}! 😊` : `${greeting}, Happy Customer! 😊`;
  const items    = (bill.items || []).map(i => ({ ...i, item_type: i.item_type || i.type, item_name: i.item_name || i.name, item_variant: i.item_variant || i.variant, staff_name: i.staff_name || i.staff, helper_name: i.helper_name || i.helper }));
  const services = items.filter(i => i.item_type === 'service');
  const products = items.filter(i => i.item_type === 'product');
  const discount = parseFloat(bill.discount || 0);
  const total    = parseFloat(bill.total || 0);
  const subtotal = total + discount;

  let msg = `${salutation}\n\nThank you for visiting us! Here's your bill summary:\n\n`;
  if (services.length > 0) {
    msg += `✂️ *Services*\n`;
    services.forEach(svc => {
      const v = svc.item_variant ? ` (${svc.item_variant})` : '';
      msg += `  • ${svc.item_name}${v} — ₹${Number(svc.price).toLocaleString('en-IN')}\n`;
    });
    msg += '\n';
  }
  if (products.length > 0) {
    msg += `🛍️ *Products*\n`;
    products.forEach(prod => {
      const qty = prod.qty || 1;
      msg += `  • ${prod.item_name} × ${qty} — ₹${Number(parseFloat(prod.price) * qty).toLocaleString('en-IN')}\n`;
    });
    msg += '\n';
  }
  if (discount > 0) {
    msg += `💰 Subtotal: ₹${Number(subtotal).toLocaleString('en-IN')}\n`;
    msg += `🎉 Discount Applied: −₹${Number(discount).toLocaleString('en-IN')}\n`;
  }
  msg += `\n✅ *Total Paid: ₹${Number(total).toLocaleString('en-IN')}*\n`;
  msg += `\n— Powered by *TOBO* 💜`;
  return msg;
}

function shareOnWhatsApp(bill) {
  const raw   = bill.customer_phone?.replace(/\D/g, '') || '';
  const phone = raw.startsWith('91') ? raw : `91${raw}`;
  const url   = `https://wa.me/${phone}?text=${encodeURIComponent(buildWhatsAppMessage(bill))}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function EditBillModal({ bill, staffList, servicesList = [], productsList = [], customerSuggestions = [], saving, onClose, onSave }) {
  const [showCustSuggestions, setShowCustSuggestions] = useState(null); // 'name' | 'phone' | null
  const [form, setForm] = useState(() => ({
    customer_name: bill.customer_name || '',
    customer_phone: bill.customer_phone || '',
    payment_method: bill.payment_method || 'cash',
    discount: bill.discount || 0,
    notes: bill.notes || '',
    items: (bill.items || []).map(item => ({
      ...item,
      item_type: item.item_type || item.type,
      item_name: item.item_name || item.name,
      item_variant: item.item_variant || item.variant || '',
      staff_id: item.staff_id || '',
      helper_staff_id: item.helper_staff_id || '',
      price: item.price || 0,
      qty: item.qty || 1,
    })),
  }));

  const [pickerState, setPickerState] = useState({ open: false, itemType: 'service' });
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setItemField = (idx, field, value) => setForm(prev => ({
    ...prev,
    items: prev.items.map((item, i) => i === idx ? { ...item, [field]: value } : item),
  }));
  const updateItemQty = (idx, direction) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== idx) return item;
        const currentQty = parseInt(item.qty, 10) || 1;
        const nextQty = direction === 'decrease' ? Math.max(1, currentQty - 1) : currentQty + 1;
        return { ...item, qty: nextQty };
      }),
    }));
  };
  const isCustomPackage = (item) => item.item_type === 'service' && Number(item.item_id) < 0;

  const subtotal = form.items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * (item.qty || 1)), 0);
  const total = Math.max(0, subtotal - (parseFloat(form.discount || 0) || 0));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
          <div>
            <h3 className="text-lg font-black text-gray-900 dark:text-white">
              {bill.isNew ? "Create Yesterday's Bill" : `Edit Bill #${bill.bill_number}`}
            </h3>
            <p className="text-xs text-gray-400">
              {bill.isNew ? "Specify customer, payment, items and attendants" : "Update package names, prices, payment and attendants"}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto max-h-[calc(90vh-145px)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative">
              <input
                value={form.customer_name}
                onChange={e => {
                  setField('customer_name', e.target.value);
                  setShowCustSuggestions('name');
                }}
                onFocus={() => setShowCustSuggestions('name')}
                onBlur={() => setTimeout(() => setShowCustSuggestions(null), 250)}
                placeholder="Customer name"
                className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {showCustSuggestions === 'name' && (form.customer_name || '').trim().length >= 1 && (
                (() => {
                  const filtered = customerSuggestions
                    .filter(c => (c.name || '').toLowerCase().includes((form.customer_name || '').toLowerCase()))
                    .slice(0, 5);
                  if (filtered.length === 0) return null;
                  return (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-800 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                      {filtered.map((c, i) => (
                        <button
                          key={i}
                          type="button"
                          onMouseDown={() => {
                            setForm(prev => ({ ...prev, customer_name: c.name, customer_phone: c.phone }));
                            setShowCustSuggestions(null);
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-purple-50 dark:hover:bg-slate-700 text-xs text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-slate-700 last:border-none flex items-center justify-between"
                        >
                          <span className="font-bold truncate">{c.name}</span>
                          <span className="text-gray-400 dark:text-slate-500 font-mono text-[10px]">{c.phone || 'No phone'}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
            <div className="relative">
              <input
                value={form.customer_phone}
                onChange={e => {
                  setField('customer_phone', e.target.value);
                  setShowCustSuggestions('phone');
                }}
                onFocus={() => setShowCustSuggestions('phone')}
                onBlur={() => setTimeout(() => setShowCustSuggestions(null), 250)}
                placeholder="Phone"
                className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {showCustSuggestions === 'phone' && (form.customer_phone || '').trim().length >= 1 && (
                (() => {
                  const filtered = customerSuggestions
                    .filter(c => (c.phone || '').includes(form.customer_phone || ''))
                    .slice(0, 5);
                  if (filtered.length === 0) return null;
                  return (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-800 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                      {filtered.map((c, i) => (
                        <button
                          key={i}
                          type="button"
                          onMouseDown={() => {
                            setForm(prev => ({ ...prev, customer_name: c.name, customer_phone: c.phone }));
                            setShowCustSuggestions(null);
                          }}
                          className="w-full text-left px-4 py-2.5 hover:bg-purple-50 dark:hover:bg-slate-700 text-xs text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-slate-700 last:border-none flex items-center justify-between"
                        >
                          <span className="font-bold truncate">{c.name}</span>
                          <span className="text-gray-400 dark:text-slate-500 font-mono text-[10px]">{c.phone || 'No phone'}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
            <select value={form.payment_method} onChange={e=>setField('payment_method', e.target.value)} className="px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
              <option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option><option value="other">Other</option>
            </select>
            <input type="number" min="0" value={form.discount} onChange={e=>setField('discount', e.target.value)} placeholder="Discount" className="px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"/>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Line Items</p>
            </div>
            {form.items.map((item, idx) => (
              <div key={item.id || idx} className="rounded-xl border border-gray-200 dark:border-slate-700 p-3 bg-gray-50 dark:bg-slate-800/60 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-gray-400 capitalize">{item.item_type}</p>
                      <div className="flex items-center gap-2">
                        {isCustomPackage(item) && <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[11px] font-bold">Custom Package</span>}
                          <button
                            type="button"
                            onClick={() => setForm(prev => ({
                              ...prev,
                              items: prev.items.filter((_, i) => i !== idx)
                            }))}
                            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                      </div>
                    </div>
                    {item.item_type === 'product' ? (
                      <select
                        value={item.item_id || ''}
                        onChange={e => {
                          const selectedId = Number(e.target.value);
                          const prod = productsList.find(p => p.id === selectedId);
                          if (prod) {
                            setItemField(idx, 'item_id', prod.id);
                            setItemField(idx, 'item_name', prod.name);
                            setItemField(idx, 'price', prod.price);
                          } else {
                            setItemField(idx, 'item_id', '');
                            setItemField(idx, 'item_name', '');
                            setItemField(idx, 'price', 0);
                          }
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">-- Select Product --</option>
                        {productsList.map(prod => (
                          <option key={prod.id} value={prod.id}>
                            {prod.name} - ₹{prod.price}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={item.item_id || ''}
                        onChange={e => {
                          const selectedId = Number(e.target.value);
                          const svc = servicesList.find(s => s.id === selectedId);
                          if (svc) {
                            setItemField(idx, 'item_id', svc.id);
                            setItemField(idx, 'item_name', svc.name);
                            setItemField(idx, 'price', svc.price);
                          } else {
                            setItemField(idx, 'item_id', '');
                            setItemField(idx, 'item_name', '');
                            setItemField(idx, 'price', 0);
                          }
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">-- Select Service --</option>
                        {Array.from(new Set(servicesList.map(s => s.category).filter(Boolean))).map(cat => (
                          <optgroup key={cat} label={cat}>
                            {servicesList.filter(s => s.category === cat).map(svc => (
                              <option key={svc.id} value={svc.id}>
                                {svc.name} - ₹{svc.price}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                        {servicesList.filter(s => !s.category).length > 0 && (
                          <optgroup label="Other">
                            {servicesList.filter(s => !s.category).map(svc => (
                              <option key={svc.id} value={svc.id}>
                                {svc.name} - ₹{svc.price}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    )}
                    <input
                      type="text"
                      value={item.item_name || ''}
                      onChange={e=>setItemField(idx, 'item_name', e.target.value)}
                      className="mt-2 w-full px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-xs text-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      placeholder="Custom / Override name (optional)"
                    />
                  </div>
                  <input type="number" min="0" value={item.price} onChange={e=>setItemField(idx, 'price', e.target.value)} className="w-28 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm font-bold text-right text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"/>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={item.item_variant || ''}
                    onChange={e=>setItemField(idx, 'item_variant', e.target.value)}
                    className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={isCustomPackage(item) ? 'Package summary' : 'Variant'}
                  />
                  <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Qty</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateItemQty(idx, 'decrease')}
                        className="w-8 h-8 rounded-full border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-gray-300 flex items-center justify-center"
                      >
                        <Minus size={14} />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={item.qty || 1}
                        onChange={e=>setItemField(idx, 'qty', e.target.value)}
                        className="w-14 px-2 py-1.5 rounded-lg bg-transparent border-0 text-center text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-0"
                        placeholder="Qty"
                      />
                      <button
                        type="button"
                        onClick={() => updateItemQty(idx, 'increase')}
                        className="w-8 h-8 rounded-full border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-gray-300 flex items-center justify-center"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </div>
                {item.item_type === 'service' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <select value={item.staff_id || ''} onChange={e=>setItemField(idx, 'staff_id', e.target.value)} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="">No attendant</option>
                      {staffList.map(staff => <option key={staff.id} value={staff.id}>{staff.name}</option>)}
                    </select>
                    <select value={item.helper_staff_id || ''} onChange={e=>setItemField(idx, 'helper_staff_id', e.target.value)} className="px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="">No helper</option>
                      {staffList.map(staff => <option key={staff.id} value={staff.id}>{staff.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPickerState({ open: true, itemType: 'service' })}
              className="flex-1 py-2 border border-dashed border-purple-300 hover:border-purple-500 rounded-xl text-xs font-bold text-purple-600 hover:text-purple-700 transition-colors"
            >
              + Add Service
            </button>
            <button
              type="button"
              onClick={() => setPickerState({ open: true, itemType: 'product' })}
              className="flex-1 py-2 border border-dashed border-purple-300 hover:border-purple-500 rounded-xl text-xs font-bold text-purple-600 hover:text-purple-700 transition-colors"
            >
              + Add Product
            </button>
          </div>

          <div className="rounded-xl border border-purple-100 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4 space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span className="font-bold text-gray-800 dark:text-white">{fmt(subtotal)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-green-600">Discount</span><span className="font-bold text-green-600">-{fmt(form.discount)}</span></div>
            <div className="flex justify-between text-base pt-1 border-t border-purple-200 dark:border-purple-800"><span className="font-black text-gray-900 dark:text-white">Total</span><span className="font-black text-purple-600">{fmt(total)}</span></div>
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-slate-700">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-600 dark:text-gray-300">Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-black disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} {bill.isNew ? "Create Bill" : "Save Changes"}
          </button>
        </div>

        {pickerState.open && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md max-h-[80vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
                <h4 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wider">
                  Select {pickerState.itemType === 'product' ? 'Products' : 'Services'}
                </h4>
                <button
                  type="button"
                  onClick={() => { setPickerState({ open: false, itemType: 'service' }); setPickerSearch(''); setSelectedItems([]); }}
                  className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="p-3 border-b border-gray-100 dark:border-slate-700 relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={pickerSearch}
                  onChange={e => setPickerSearch(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  autoFocus
                />
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {(pickerState.itemType === 'product' ? productsList : servicesList)
                  .filter(obj => !pickerSearch || obj.name.toLowerCase().includes(pickerSearch.toLowerCase()))
                  .map(obj => {
                    const isSelected = selectedItems.includes(obj.id);
                    return (
                      <button
                        key={obj.id}
                        type="button"
                        onClick={() => {
                          setSelectedItems(prev => prev.includes(obj.id) ? prev.filter(id => id !== obj.id) : [...prev, obj.id]);
                        }}
                        className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left border transition-all ${
                          isSelected
                            ? 'border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-900/10 text-purple-700 dark:text-purple-400'
                            : 'border-transparent hover:bg-gray-50 dark:hover:bg-slate-800/40 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm font-bold">{obj.name}</span>
                        </div>
                        <span className="text-xs font-black">₹{obj.price}</span>
                      </button>
                    );
                  })}
              </div>

              {selectedItems.length > 0 && (
                <div className="p-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/40">
                  <button
                    type="button"
                    onClick={() => {
                      const list = pickerState.itemType === 'product' ? productsList : servicesList;
                      const chosen = list.filter(obj => selectedItems.includes(obj.id));
                      const newItems = chosen.map(obj => ({
                        item_id: obj.id,
                        item_type: pickerState.itemType,
                        item_name: obj.name,
                        price: obj.price,
                        qty: 1,
                        staff_id: '',
                        helper_staff_id: ''
                      }));
                      setForm(prev => {
                        const cleanItems = (prev.items.length === 1 && prev.items[0].item_name === '') ? [] : prev.items;
                        return {
                          ...prev,
                          items: [...cleanItems, ...newItems]
                        };
                      });
                      setPickerState({ open: false, itemType: 'service' });
                      setSelectedItems([]);
                      setPickerSearch('');
                    }}
                    className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm rounded-xl transition-all shadow-md"
                  >
                    Add {selectedItems.length} Selected {pickerState.itemType === 'product' ? 'Products' : 'Services'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Bill Row ──────────────────────────────────────────────────────────────
function BillRow({ bill, index, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const meta     = PAYMENT_META[bill.payment_method] || PAYMENT_META.cash;
  const { Icon } = meta;
  const items    = (bill.items || []).map(i => ({ ...i, item_type: i.item_type || i.type, item_name: i.item_name || i.name, item_variant: i.item_variant || i.variant, staff_name: i.staff_name || i.staff, helper_name: i.helper_name || i.helper }));
  const services = items.filter(i => i.item_type === 'service');
  const products = items.filter(i => i.item_type === 'product');
  const discount = parseFloat(bill.discount || 0);

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden transition-all hover:shadow-sm`}>
      {/* Main row */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors text-left"
      >

        {/* Index badge */}
        <div className="w-11 h-11 rounded-2xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-black text-purple-600 dark:text-purple-400">#{bill.bill_number}</span>
        </div>

        {/* Customer info */}
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-gray-800 dark:text-white truncate">
            {bill.customer_name || 'Walk-in Customer'}
          </p>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {bill.customer_phone && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Phone size={10}/>{bill.customer_phone}
              </span>
            )}
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock size={10}/>{fmtTime(bill.created_at)}
            </span>
            <span className="text-xs text-gray-400">
              {services.length > 0 && `${services.length} service${services.length > 1 ? 's' : ''}`}
              {services.length > 0 && products.length > 0 && ' · '}
              {products.length > 0 && `${products.length} product${products.length > 1 ? 's' : ''}`}
            </span>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl border ${meta.light}`}>
            <Icon size={12}/> {meta.label.toUpperCase()}
          </span>
          <div className="text-right">
            <p className="text-lg font-black text-gray-800 dark:text-white">{fmt(bill.total)}</p>
            {discount > 0 && (
              <p className="text-xs text-green-500 font-semibold">-{fmt(discount)} off</p>
            )}
          </div>
          <div className={`w-7 h-7 rounded-xl flex items-center justify-center transition-colors ${expanded ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-100 dark:bg-slate-700'}`}>
            {expanded
              ? <ChevronUp size={14} className="text-purple-600 dark:text-purple-400"/>
              : <ChevronDown size={14} className="text-gray-400"/>
            }
          </div>
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40">
          <div className="px-5 py-4 space-y-4">
            {services.length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Services</p>
                <div className="space-y-2">
                  {services.map((svc, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                          <Scissors size={12} className="text-purple-500"/>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate">
                            {svc.item_name}{svc.item_variant ? ` · ${svc.item_variant}` : ''}
                          </p>
                          {svc.staff_name && (
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <User size={9}/>{svc.staff_name}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-black text-gray-800 dark:text-white flex-shrink-0">{fmt(svc.price)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {products.length > 0 && (
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Products</p>
                <div className="space-y-2">
                  {products.map((prod, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                          <Package size={12} className="text-blue-500"/>
                        </div>
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate">
                          {prod.item_name} <span className="text-gray-400 font-normal">× {prod.qty || 1}</span>
                        </p>
                      </div>
                      <span className="text-sm font-black text-gray-800 dark:text-white flex-shrink-0">{fmt(parseFloat(prod.price) * (prod.qty || 1))}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bill totals */}
            <div className="border-t border-gray-200 dark:border-slate-600 pt-3 space-y-1.5">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Subtotal</span>
                <span>{fmt(parseFloat(bill.total) + discount)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><Tag size={11}/>Discount</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">−{fmt(discount)}</span>
                </div>
              )}
              <div className="flex justify-between font-black text-base pt-1">
                <span className="text-gray-800 dark:text-white">Total Paid</span>
                <span className="text-purple-600 dark:text-purple-400">{fmt(bill.total)}</span>
              </div>
            </div>

            <button
              onClick={() => onEdit(bill)}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white font-bold text-sm py-2.5 rounded-xl transition-colors"
            >
              <Edit2 size={15} />
              Edit Bill
            </button>

            {/* WhatsApp Share — only when phone number is present */}
            {bill.customer_phone && (
              <button
                onClick={() => shareOnWhatsApp(bill)}
                className="mt-3 w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white font-bold text-sm py-2.5 rounded-xl transition-colors"
              >
                <MessageCircle size={15} />
                Share Bill on WhatsApp
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────
export default function SalonTodayBills() {
  const { userToken: token } = useAuth();
  const [bills,     setBills]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [editingBill, setEditingBill] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // New States for Toggler
  const [timeView, setTimeView] = useState(() => localStorage.getItem('salon_billing_date_view') || 'today'); // 'today' or 'yesterday'
  const [customerSuggestions, setCustomerSuggestions] = useState([]);

  useEffect(() => {
    localStorage.setItem('salon_billing_date_view', timeView);
  }, [timeView]);


  const loadBills = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const targetDate = new Date();
      if (timeView === 'yesterday') {
        targetDate.setDate(targetDate.getDate() - 1);
      }
      const dateStr = getIstDateParam(targetDate);
      const res  = await fetch(
        `${SALON_API_URL}/bills?from_date=${encodeURIComponent(dateStr)}&to_date=${encodeURIComponent(dateStr)}&limit=1000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        const filteredBills = (data.data || [])
          .filter(b => getIstDateParam(b.created_at) === dateStr)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setBills(filteredBills);
        setLastFetch(new Date());
      }
    } catch (err) {
      console.error('Could not load bills:', err);
    } finally {
      setLoading(false);
    }
  }, [token, timeView]);

  const loadStaff = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(SALON_API_URL + '/staff', { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (data.success) setStaffList(data.data || []);
    } catch (err) {
      console.error('Could not load staff:', err);
    }
  }, [token]);

  const loadServices = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(SALON_API_URL + '/services', { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (data.success) setServicesList(data.data || []);
    } catch (err) {
      console.error('Could not load services:', err);
    }
  }, [token]);

  const loadProducts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(SALON_API_URL + '/products?inStock=true', { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (data.success) setProductsList(data.data || []);
    } catch (err) {
      console.error('Could not load products:', err);
    }
  }, [token]);

  const handleSaveEdit = async (form) => {
    if (!editingBill) return;
    setSavingEdit(true);
    try {
      const isNew = editingBill.isNew;
      const payload = {
        customer_name: form.customer_name || null,
        customer_phone: form.customer_phone || null,
        payment_method: form.payment_method || 'cash',
        discount: parseFloat(form.discount || 0) || 0,
        notes: form.notes || null,
        items: form.items.map(item => ({
          id: isNew ? undefined : item.id,
          item_type: item.item_type || 'service',
          item_name: item.item_name || '',
          item_variant: item.item_variant || null,
          price: parseFloat(item.price || 0) || 0,
          qty: parseInt(item.qty, 10) || 1,
          staff_id: item.staff_id || null,
          helper_staff_id: item.helper_staff_id || null,
        })),
        createdDate: isNew ? 'yesterday' : undefined
      };
      const url = isNew ? `${SALON_API_URL}/bills` : `${SALON_API_URL}/bills/${editingBill.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method: method,
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success(isNew ? 'Bill successfully generated for yesterday' : 'Bill updated');
      setEditingBill(null);
      await loadBills(true);
    } catch (err) {
      toast.error(err.message || 'Failed to save bill');
    } finally {
      setSavingEdit(false);
    }
  };

  const loadCustomers = useCallback(async () => {
    if (!token) return;
    try {
      const [billsRes, leadsRes] = await Promise.all([
        fetch(`${SALON_API_URL}/bills?limit=150`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${SALON_API_URL}/leads`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);
      
      let billsData = { success: false, data: [] };
      if (billsRes && billsRes.ok) {
        billsData = await billsRes.json();
      }
      
      let leadsData = { success: false, data: [] };
      if (leadsRes && leadsRes.ok) {
        leadsData = await leadsRes.json();
      }

      const uniqueCustomers = [];
      const seen = new Set();

      const addCustomer = (name, phone) => {
        const cleanName = (name || '').trim();
        const cleanPhone = (phone || '').trim();
        if (!cleanName && !cleanPhone) return;
        const key = `${cleanName.toLowerCase()}|||${cleanPhone}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueCustomers.push({ name: cleanName, phone: cleanPhone });
        }
      };

      if (billsData.success && Array.isArray(billsData.data)) {
        billsData.data.forEach(bill => {
          addCustomer(bill.customer_name, bill.customer_phone);
        });
      }

      if (leadsData.success && Array.isArray(leadsData.data)) {
        leadsData.data.forEach(lead => {
          addCustomer(lead.name, lead.phone);
        });
      }

      setCustomerSuggestions(uniqueCustomers);
    } catch (err) {
      console.error('Failed to load customer suggestions', err);
    }
  }, [token]);

  useEffect(() => {
    loadBills();
    loadStaff();
    loadServices();
    loadProducts();
    loadCustomers();
  }, [loadBills, loadStaff, loadServices, loadProducts, loadCustomers]);

  useEffect(() => {
    if (editingBill && token) {
      loadStaff();
      loadServices();
      loadProducts();
      loadCustomers();
    }
  }, [editingBill, token, loadStaff, loadServices, loadProducts, loadCustomers]);

  // Silent auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => loadBills(true), 60_000);
    return () => clearInterval(interval);
  }, [loadBills]);

  // ── Computed stats ─────────────────────────────────────────────────────
  const totalRevenue  = bills.reduce((s, b) => s + parseFloat(b.total || 0), 0);
  const avgBill       = bills.length > 0 ? totalRevenue / bills.length : 0;

  let cashAmount = 0, upiAmount = 0, cardAmount = 0;
  bills.forEach(b => {
    const total = parseFloat(b.total || 0);
    if (b.payment_method === 'cash') cashAmount += total;
    else if (b.payment_method === 'upi') upiAmount += total;
    else if (b.payment_method === 'card') cardAmount += total;
    else if (b.payment_method === 'split' && b.payment_split) {
      try {
        const split = typeof b.payment_split === 'string' ? JSON.parse(b.payment_split) : b.payment_split;
        cashAmount += parseFloat(split.cash || 0);
        upiAmount += parseFloat(split.upi || 0);
        cardAmount += parseFloat(split.card || 0);
      } catch (e) {
        cashAmount += total;
      }
    } else {
      cashAmount += total;
    }
  });


  const todayDate = new Date().toLocaleDateString('en-IN', {
    timeZone: IST_TIME_ZONE,
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const yesterdayDate = new Date(Date.now() - 24 * 60 * 60 * 1000).toLocaleDateString('en-IN', {
    timeZone: IST_TIME_ZONE,
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="p-6 lg:p-8 min-h-full w-full">
      <ToastContainer position="top-right" autoClose={3000} theme="colored"/>

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 dark:text-white flex items-center gap-3">
            <span>{timeView === 'today' ? "Today's Bills" : "Yesterday's Bills"}</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-black uppercase tracking-wider ${
              timeView === 'today'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200/50'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200/50'
            }`}>
              {timeView === 'today' ? 'Active' : 'Yesterday'}
            </span>
          </h1>
          <div className={`text-sm mt-3.5 flex items-center gap-2 px-3 py-2 rounded-xl border w-fit shadow-sm ${
            timeView === 'today'
              ? 'bg-emerald-50/40 border-emerald-100/80 text-emerald-850 dark:bg-emerald-950/20 dark:border-emerald-900/30 dark:text-emerald-300'
              : 'bg-amber-50/40 border-amber-100/80 text-amber-850 dark:bg-amber-950/20 dark:border-amber-900/30 dark:text-amber-300'
          }`}>
            <Clock size={15} className="opacity-80"/>
            <span className="font-extrabold">{timeView === 'today' ? todayDate : yesterdayDate}</span>
            <span className="opacity-40">·</span>
            <span className="font-semibold text-xs uppercase tracking-wider opacity-90">12:00 AM → 11:59 PM</span>
          </div>
        </div>

        {/* Toggler & Create Action */}
        <div className="flex items-center gap-4 self-start sm:self-center">
          {/* Today / Yesterday Toggler */}
          <div className="flex items-center bg-gray-100 dark:bg-slate-800 p-1 rounded-2xl border border-gray-200/50 dark:border-slate-700/50 shadow-sm">
            <button
              onClick={() => setTimeView('today')}
              className={`px-4 py-2 rounded-xl text-sm font-black transition-all ${
                timeView === 'today'
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setTimeView('yesterday')}
              className={`px-4 py-2 rounded-xl text-sm font-black transition-all ${
                timeView === 'yesterday'
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Yesterday
            </button>
          </div>

          {/* New Bill Button (Only on Yesterday view) */}
          {timeView === 'yesterday' && (
            <button
              onClick={() => setEditingBill({
                isNew: true,
                bill_number: 'New',
                customer_name: '',
                customer_phone: '',
                payment_method: 'cash',
                discount: 0,
                notes: '',
                items: []
              })}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition-all shadow-md hover:shadow-lg"
            >
              <Plus size={16} />
              New Bill
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Loader2 size={36} className="animate-spin text-purple-600"/>
          <p className="text-gray-400 font-medium">
            {timeView === 'today' ? "Loading today's bills…" : "Loading yesterday's bills…"}
          </p>
        </div>
      ) : (
        <div className="space-y-6">

          {/* ── Primary Stat Cards ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              title="Total Bills"
              value={bills.length}
              sub={timeView === 'today' ? "Generated today" : "Generated yesterday"}
              icon={Hash}
              gradient="bg-gradient-to-br from-purple-600 to-violet-700"
              iconBg="bg-white/20"
            />
            <StatCard
              title="Total Revenue"
              value={fmt(totalRevenue)}
              sub={`Avg ${fmt(Math.round(avgBill))} per bill`}
              icon={TrendingUp}
              gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
              iconBg="bg-white/20"
            />
          </div>

          {/* ── Payment Breakdown Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Banknote size={24} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-gray-400 uppercase tracking-widest">Cash Collection</p>
                <p className="text-xl font-black text-gray-800 dark:text-white mt-1">{fmt(cashAmount)}</p>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Smartphone size={24} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-gray-400 uppercase tracking-widest">UPI Collection</p>
                <p className="text-xl font-black text-gray-800 dark:text-white mt-1">{fmt(upiAmount)}</p>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-50 dark:bg-violet-900/20 text-violet-650 flex items-center justify-center flex-shrink-0">
                <CreditCard size={24} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-gray-400 uppercase tracking-widest">Card Collection</p>
                <p className="text-xl font-black text-gray-800 dark:text-white mt-1">{fmt(cardAmount)}</p>
              </div>
            </div>
          </div>


          {/* ── Bill list ── */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-bold text-gray-700 dark:text-gray-300">
                  {bills.length} Bill{bills.length !== 1 ? 's' : ''} {timeView === 'today' ? 'Today' : 'Yesterday'}
                </h2>
                {lastFetch && (
                  <p className="text-xs text-gray-400">
                    Updated at {lastFetch.toLocaleTimeString('en-IN', { timeZone: IST_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                )}
              </div>
            </div>

            {bills.length === 0 ? (
              <div className="text-center py-24 bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700">
                <div className="w-20 h-20 rounded-3xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                  <ReceiptText size={32} className="text-gray-300 dark:text-slate-500"/>
                </div>
                <h3 className="text-xl font-black text-gray-500 dark:text-gray-400">
                  No bills yet {timeView === 'today' ? 'today' : 'yesterday'}
                </h3>
                <p className="text-sm text-gray-400 mt-2">
                  {timeView === 'today'
                    ? 'Bills will appear here as you create them in Billing'
                    : 'No bills were generated yesterday'}
                </p>
                <div className="mt-6">
                  {timeView === 'today' ? (
                    <Link
                      to="/salon/billing"
                      className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-all shadow-sm"
                    >
                      <Plus size={16} />
                      Create First Bill
                    </Link>
                  ) : (
                    <button
                      onClick={() => setEditingBill({
                        isNew: true,
                        bill_number: 'New',
                        customer_name: '',
                        customer_phone: '',
                        payment_method: 'cash',
                        discount: 0,
                        notes: '',
                        items: []
                      })}
                      className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm px-5 py-2.5 rounded-xl transition-all shadow-sm"
                    >
                      <Plus size={16} />
                      Create Yesterday's Bill
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {bills.map((bill, i) => (
                  <BillRow
                    key={bill.id || bill.bill_number}
                    bill={bill}
                    index={i}
                    onEdit={setEditingBill}
                  />
                ))}
              </div>
            )}
          </div>

        </div>
      )}
      {editingBill && (
        <EditBillModal
          bill={editingBill}
          staffList={staffList}
          servicesList={servicesList}
          productsList={productsList}
          customerSuggestions={customerSuggestions}
          saving={savingEdit}
          onClose={() => setEditingBill(null)}
          onSave={handleSaveEdit}
        />
      )}
    </div>
  );
}